<template>
  <NotFoundView
    v-if="showSeasonNotFound"
    :heading="seasonNotFoundHeading"
    :message="seasonNotFoundMessage"
  />
  <main v-else class="single-season-prototype">
    <section class="hero g-0 position-relative">
      <HeroTopPanel
        :logo-src="assets.logo"
        :background-src="backgroundUrl"
        :mobile-background-src="mobileBackgroundUrl"
        :background-wrap-style="heroBackgroundStyle"
        :login-path="loginPath"
        @open-mobile-search="openMobileSearchOverlay"
      >
        <div class="row g-0 hero__grid align-items-end">
          <div class="col-auto hero__posterWrap">
            <a
              class="hero__posterLink"
              data-testid="season-poster-link"
              :href="parentShowSeasonsPath"
            >
              <div
                :class="seasonPosterFrameClasses"
                data-testid="season-poster-frame"
              >
                <div
                  v-if="seasonPosterLoading"
                  class="image-load-frame__skeleton"
                  data-testid="season-poster-skeleton"
                  aria-hidden="true"
                ></div>
                <img
                  v-if="seasonPosterImageSrc"
                  :class="seasonPosterImageClasses"
                  :src="seasonPosterImageSrc"
                  :alt="`${showTitle} ${seasonTitle} poster`"
                  loading="eager"
                  decoding="async"
                  fetchpriority="high"
                  @load="handleSeasonPosterLoad"
                  @error="handleSeasonPosterError"
                >
              </div>
            </a>
          </div>
          <div class="col hero__meta">
            <div class="upper-container">
              <div v-if="hasLoadedSeasonPayload" class="user-event-container">
                <h1 :class="['item-title', titleLengthClass]" data-testid="season-show-title">
                  <a
                    class="season-show-title-link"
                    data-testid="season-show-title-link"
                    :href="parentShowPath"
                  >
                    {{ showTitle }}
                  </a>
                  <span class="show-release-year font-darker-gray weight300">{{ showYearDisplay }}</span>
                </h1>
                <h2 class="season-title" data-testid="season-heading">
                  {{ seasonTitle }}
                  <span v-if="seasonYearDisplay" class="season-release-year font-darker-gray weight300">
                    • {{ seasonYearDisplay }}
                  </span>
                </h2>
                <div class="buttons">
                  <template v-if="isAuthenticated">
                    <button
                      class="btn single-btn season-fav-btn"
                      :class="{ 'btn--favourited': isSeasonFavourited }"
                      data-testid="season-favourite-action"
                      type="button"
                      :disabled="seasonFavouritePending"
                      @click="toggleSeasonFavourite"
                    >
                      <div class="default-btn">
                        <span class="fav-btn"><i class="ion-heart"></i> {{ favouriteButtonText }}</span>
                        <span class="fav-btn-sm"><i class="ion-heart"></i>{{ favouriteButtonShortText }}</span>
                      </div>
                    </button>
                  </template>
                  <template v-else>
                    <a class="btn single-btn season-fav-btn" data-testid="season-favourite-action" :href="loginPath">
                      <div class="default-btn">
                        <span class="fav-btn"><i class="ion-heart"></i> Add to favourites</span>
                        <span class="fav-btn-sm"><i class="ion-heart"></i>Favourite</span>
                      </div>
                    </a>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </HeroTopPanel>
    </section>

    <div v-if="hasLoadedSeasonPayload" class="container g-0 hero-tablet-controls">
      <div class="row g-0 hero-tablet-controls__row">
        <div class="col g-0">
          <div class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="btn single-btn season-fav-btn"
                :class="{ 'btn--favourited': isSeasonFavourited }"
                type="button"
                :disabled="seasonFavouritePending"
                @click="toggleSeasonFavourite"
              >
                <div class="default-btn">
                  <span class="fav-btn"><i class="ion-heart"></i> {{ favouriteButtonText }}</span>
                  <span class="fav-btn-sm"><i class="ion-heart"></i>{{ favouriteButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn single-btn season-fav-btn" :href="loginPath">
                <div class="default-btn">
                  <span class="fav-btn"><i class="ion-heart"></i> Add to favourites</span>
                  <span class="fav-btn-sm"><i class="ion-heart"></i>Favourite</span>
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
    </div>

    <section v-if="hasLoadedSeasonPayload" class="mobile g-0">
      <div class="row g-0 item-title-mobile">
        <h1 :class="titleLengthClass">
          <a
            class="season-show-title-link"
            data-testid="season-mobile-show-title-link"
            :href="parentShowPath"
          >
            {{ showTitle }}
          </a>
          <span class="release-year font-darker-gray weight300">{{ showYearDisplay }}</span>
        </h1>
        <h2 class="season-title">
          {{ seasonTitle }}
          <span v-if="seasonYearDisplay" class="release-year font-darker-gray weight300">
            • {{ seasonYearDisplay }}
          </span>
        </h2>
      </div>
      <div class="row g-0">
        <div class="col g-0">
          <div class="buttons">
            <template v-if="isAuthenticated">
              <button
                class="btn single-btn season-fav-btn"
                :class="{ 'btn--favourited': isSeasonFavourited }"
                type="button"
                :disabled="seasonFavouritePending"
                @click="toggleSeasonFavourite"
              >
                <div class="default-btn">
                  <span class="fav-btn"><i class="ion-heart"></i> {{ favouriteButtonText }}</span>
                  <span class="fav-btn-sm"><i class="ion-heart"></i>{{ favouriteButtonShortText }}</span>
                </div>
              </button>
            </template>
            <template v-else>
              <a class="btn single-btn season-fav-btn" :href="loginPath">
                <div class="default-btn">
                  <span class="fav-btn"><i class="ion-heart"></i> Add to favourites</span>
                  <span class="fav-btn-sm"><i class="ion-heart"></i>Favourite</span>
                </div>
              </a>
            </template>
          </div>
        </div>
      </div>
    </section>
    <section
      v-else
      class="mobile g-0 mobile-hero-placeholder"
      data-testid="season-mobile-hero-placeholder"
      aria-hidden="true"
    ></section>

    <a class="top-link" id="top"></a>
    <div class="container g-0 mobile-nav">
      <div class="row">
        <div class="col">
          <nav class="nav">
            <ul class="film-nav">
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'overview' }"
                  data-testid="season-tab-overview"
                  :to="basePath"
                >Overview</RouterLink>
              </li>
              <li>
                <RouterLink
                  :class="{ active: activeSection === 'comments' }"
                  :to="`${basePath}/comments`"
                >Comments</RouterLink>
              </li>
              <li v-if="isAuthenticated">
                <RouterLink
                  :class="{ active: activeSection === 'you' }"
                  data-testid="season-tab-you"
                  :to="`${basePath}/you`"
                >You</RouterLink>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <div class="container g-0 season-nav-container">
      <div class="row g-0">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <div class="container g-0">
            <div class="row g-0 next-prev-seasons">
              <div class="col-6 g-0 prev-season-row">
                <div>
                  <a
                    v-if="prevSeason"
                    class="next-season"
                    data-testid="season-prev-link"
                    :href="prevSeason.href"
                  >
                    <span class="ion-android-arrow-back"></span> {{ prevSeason.label }}
                  </a>
                </div>
              </div>
              <div class="col-6 g-0 next-season-row">
                <div>
                  <a
                    v-if="nextSeason"
                    class="next-season"
                    data-testid="season-next-link"
                    :href="nextSeason.href"
                  >
                    {{ nextSeason.label }} <span class="ion-android-arrow-forward"></span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="activeSection === 'overview'" class="container g-0">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-9">
          <div class="container g-0">
            <div class="row g-0">
              <div class="col g-0">
                <div class="season-overview">
                  <p data-testid="season-overview">{{ overview }}</p>
                </div>
                <div class="episodes-count-title-container episodes-count-toolbar">
                  <h2 class="episodes-count-title" data-testid="season-episode-count">{{ episodeCountText }}</h2>
                  <template v-if="seasonBulkWatchActionVisible">
                    <button
                      v-if="isAuthenticated"
                      class="season-bulk-watch-btn"
                      data-testid="season-bulk-watch-action"
                      type="button"
                      :disabled="seasonBulkWatchPending"
                      @click="markSeasonAsWatched"
                    >
                      {{ seasonBulkWatchButtonText }}
                    </button>
                    <a
                      v-else
                      class="season-bulk-watch-btn"
                      data-testid="season-bulk-watch-action"
                      :href="loginPath"
                    >
                      {{ seasonBulkWatchButtonText }}
                    </a>
                  </template>
                </div>
              </div>
            </div>

            <div class="row g-0">
              <div class="col g-0 listing-methods">
                <button
                  v-for="button in listingButtons"
                  :key="button.key"
                  :class="['listing-method-button', button.key === activeListingMode ? 'active' : null]"
                  :aria-label="button.label"
                  data-testid="season-listing-button"
                  :title="button.label"
                  type="button"
                  @click="setListingMode(button.key)"
                >
                  <ViewModeIcon :mode="button.key" data-testid="season-listing-button-icon" />
                </button>
              </div>
            </div>

            <template v-if="episodes.length > 0">
              <template v-if="activeListingMode === '4'">
                <div
                  v-for="(episode, index) in episodes"
                  :key="`episode-text-${episode.numberText}`"
                  :class="['row', 'g-0', 'episode-container-row-lm', 'text-only', index === episodes.length - 1 ? 'last-row' : null]"
                  data-testid="season-text-episode-row"
                >
                  <div class="col-12 g-0">
                    <div class="episode-data-lm">
                      <div class="episode-number-lm">
                        <p data-testid="season-text-episode-number">{{ episode.numberOnly }}</p>
                      </div>
                      <div class="episode-title-aired">
                        <h3 class="ep-title-list" data-testid="season-text-episode-title">
                          <a :href="episode.href || '#'">{{ episode.titleOnly }}</a>
                        </h3>
                        <p class="ep-aired-text-list" data-testid="season-text-episode-meta">{{ episode.metaText }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <div v-else-if="activeListingMode === '2'" class="row g-0">
                <div class="col g-0">
                  <div class="posters-list">
                    <div
                      v-for="episode in episodes"
                      :key="`episode-cover-${episode.numberText}`"
                      class="poster-only"
                      data-testid="season-cover-episode"
                    >
                      <a :href="episode.href || '#'">
                        <img :src="episode.imageSrc" :alt="`${showTitle} ${seasonTitle} ${episode.numberText} image`">
                        <span class="poster-episode-title" data-testid="season-cover-episode-title">
                          <span class="poster-episode-title__heading" data-testid="season-cover-episode-title-row">
                            {{ episode.numberOnly }} • {{ episode.titleOnly }}
                          </span>
                          <span class="poster-episode-title__meta" data-testid="season-cover-episode-meta-row">
                            {{ episode.metaText }}
                          </span>
                        </span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <template v-else-if="activeListingMode === '3'">
                <div
                  v-for="(episode, index) in episodes"
                  :key="`episode-list-${episode.numberText}`"
                >
                  <div
                    v-if="!useCompactListMode"
                    :class="[
                      'row',
                      'g-0',
                      'episode-container-row-lm',
                      'simple-row',
                      index === 0 ? 'first-row' : null,
                      index === episodes.length - 1 ? 'last-row' : null,
                    ]"
                    data-testid="season-list-episode-row"
                  >
                    <div class="col-6 g-0">
                      <div class="episode-data-lm">
                        <div class="episode-number-lm">
                          <p>{{ episode.numberOnly }}</p>
                        </div>
                        <div class="episode-title-aired">
                          <h3 class="ep-title-list" data-testid="season-list-episode-title">
                            <a :href="episode.href || '#'">{{ episode.titleOnly }}</a>
                          </h3>
                          <p class="ep-aired-text-list" data-testid="season-list-episode-meta">{{ episode.metaText }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-1 g-0 ep-buttons-lm">
                      <template v-if="isAuthenticated">
                        <button
                          class="default-btn fs-ep-btn ep-left-btn"
                          :class="{ 'btn--watched': isSeasonEpisodeWatched(episode) }"
                          data-testid="season-list-episode-watch-action"
                          type="button"
                          :disabled="seasonEpisodeWatchPending[episodeInteractionCode(episode)]"
                          @click="markSeasonEpisodeWatched(episode)"
                        >
                          <i :class="seasonEpisodeWatchIconClass(episode)"></i>
                        </button>
                      </template>
                      <template v-else>
                        <a class="default-btn" data-testid="season-list-episode-watch-action" :href="loginPath">
                          <div class="fs-ep-btn ep-left-btn">
                            <i class="ion-plus"></i>
                          </div>
                        </a>
                      </template>
                      <template v-if="isAuthenticated">
                        <button
                          class="default-btn fs-ep-btn ep-right-btn"
                          :class="{ 'btn--favourited': isSeasonEpisodeFavourited(episode) }"
                          data-testid="season-list-episode-favourite-action"
                          type="button"
                          :disabled="seasonEpisodeFavouritePending[episodeInteractionCode(episode)]"
                          @click="toggleSeasonEpisodeFavourite(episode)"
                        >
                          <i class="ion-heart"></i>
                        </button>
                      </template>
                      <template v-else>
                        <a class="default-btn" data-testid="season-list-episode-favourite-action" :href="loginPath">
                          <div class="fs-ep-btn ep-right-btn">
                            <i class="ion-heart"></i>
                          </div>
                        </a>
                      </template>
                    </div>
                    <div class="col-5 g-0 ep-rating-row-lm">
                      <div class="ep-rating-lm" @mouseleave="resetSeasonEpisodeRatingPreview(episode)">
                        <div>
                          <template v-if="isAuthenticated">
                            <button
                              v-for="star in 10"
                              :key="`list-desktop-${index}-${star}`"
                              data-testid="season-list-episode-rating-star"
                              type="button"
                              @mouseenter="previewSeasonEpisodeRating(episode, star)"
                              @click="setSeasonEpisodeRating(episode, star)"
                            >
                              <i :class="seasonEpisodeRatingStarClass(episode, star)"></i>
                            </button>
                          </template>
                          <template v-else>
                            <a
                              v-for="star in 10"
                              :key="`list-desktop-${index}-${star}`"
                              data-testid="season-list-episode-rating-star"
                              :href="loginPath"
                            >
                              <i class="rating-star ion-ios-star-outline"></i>
                            </a>
                          </template>
                        </div>
                        <div class="ep-current-rating-col">
                          <p class="ep-current-rating" data-testid="season-list-episode-rating-text">
                            {{ seasonEpisodeRatingText(episode) }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    v-else
                    :class="[
                      'row',
                      'g-0',
                      'episode-container-row-lm',
                      'narrow-row',
                      index === 0 ? 'first-row' : null,
                      index === episodes.length - 1 ? 'last-row' : null,
                    ]"
                    data-testid="season-list-episode-row"
                  >
                    <div class="col-7 g-0">
                      <div class="episode-data-lm">
                        <div class="episode-title-aired">
                          <h3 class="ep-title-list">
                            <a :href="episode.href || '#'">
                              <span class="small-ep-number-lm">{{ episode.numberOnly }} • </span>{{ episode.titleOnly }}
                            </a>
                          </h3>
                          <p class="ep-aired-text-list">{{ episode.metaText }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="col-5 g-0 ep-buttons-lm">
                      <div class="ep-actions">
                        <div class="ep-buttons">
                          <template v-if="isAuthenticated">
                            <button
                              class="default-btn fs-ep-btn ep-left-btn"
                              :class="{ 'btn--watched': isSeasonEpisodeWatched(episode) }"
                              data-testid="season-list-episode-watch-action"
                              type="button"
                              :disabled="seasonEpisodeWatchPending[episodeInteractionCode(episode)]"
                              @click="markSeasonEpisodeWatched(episode)"
                            >
                              <i :class="seasonEpisodeWatchIconClass(episode)"></i>
                            </button>
                          </template>
                          <template v-else>
                            <a class="default-btn" data-testid="season-list-episode-watch-action" :href="loginPath">
                              <div class="fs-ep-btn ep-left-btn">
                                <i class="ion-plus"></i>
                              </div>
                            </a>
                          </template>
                          <template v-if="isAuthenticated">
                            <button
                              class="default-btn fs-ep-btn ep-right-btn"
                              :class="{ 'btn--favourited': isSeasonEpisodeFavourited(episode) }"
                              type="button"
                              :disabled="seasonEpisodeFavouritePending[episodeInteractionCode(episode)]"
                              @click="toggleSeasonEpisodeFavourite(episode)"
                            >
                              <i class="ion-heart"></i>
                            </button>
                          </template>
                          <template v-else>
                            <a class="default-btn" :href="loginPath">
                              <div class="fs-ep-btn ep-right-btn">
                                <i class="ion-heart"></i>
                              </div>
                            </a>
                          </template>
                        </div>
                        <div class="ep-rating-lm ep-rating-lm--single">
                          <template v-if="isAuthenticated">
                            <button
                              class="single-star compact-rating-open"
                              data-testid="season-list-episode-rating-summary-star"
                              type="button"
                              :disabled="seasonEpisodeRatingPending[episodeInteractionCode(episode)]"
                              @click="openCompactSeasonEpisodeRatingPicker(episode)"
                            >
                              <i :class="seasonEpisodeCompactRatingStarClass(episode)"></i>
                            </button>
                          </template>
                          <template v-else>
                            <a
                              class="single-star compact-rating-open"
                              data-testid="season-list-episode-rating-summary-star"
                              :href="loginPath"
                            >
                              <i :class="seasonEpisodeCompactRatingStarClass(episode)"></i>
                            </a>
                          </template>
                          <div class="ep-current-rating-col">
                            <p class="ep-current-rating" data-testid="season-list-episode-rating-text">
                              {{ seasonEpisodeRatingText(episode) }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <template v-else>
                <div
                  v-for="(episode, index) in episodes"
                  :key="`episode-${episode.numberText}`"
                >
                  <div
                    :class="['row', 'g-0', 'episode-container', index === episodes.length - 1 ? 'last-episode-row' : null]"
                    data-testid="season-episode-row"
                  >
                    <div class="col-4 g-0 ep-image-container">
                      <a :href="episode.href || '#'">
                        <img class="ep-image" :src="episode.imageSrc" :alt="`${showTitle} ${seasonTitle} ${episode.numberText} image`">
                      </a>
                      <p class="ep-aired-text-sm">{{ episode.metaText }}</p>
                    </div>
                    <div class="col-8 g-0 ep-text">
                      <div class="container g-0">
                        <div class="row g-0">
                          <div class="col g-0 ep-title-container">
                            <h3 class="ep-title" data-testid="season-episode-title">
                              <a :href="episode.href || '#'">{{ episode.numberText }}</a>
                            </h3>
                          </div>
                        </div>
                        <div class="row g-0">
                          <div class="col g-0">
                            <span class="ep-aired-text" data-testid="season-episode-meta">{{ episode.metaText }}</span>
                          </div>
                        </div>
                        <div class="row g-0">
                          <div class="col-10 g-0 ep-buttons">
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-ep-btn ep-left-btn"
                                :class="{ 'btn--watched': isSeasonEpisodeWatched(episode) }"
                                data-testid="season-episode-watch-action"
                                type="button"
                                :disabled="seasonEpisodeWatchPending[episodeInteractionCode(episode)]"
                                @click="markSeasonEpisodeWatched(episode)"
                              >
                                <div class="default-btn big-watched">
                                  <i :class="seasonEpisodeWatchIconClass(episode)"></i> {{ seasonEpisodeWatchButtonText(episode) }}
                                </div>
                                <div class="default-btn sm-watched">
                                  <i :class="seasonEpisodeWatchIconClass(episode)"></i> {{ seasonEpisodeWatchButtonText(episode, true) }}
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a
                                class="fs-ep-btn ep-left-btn"
                                data-testid="season-episode-watch-action"
                                :href="loginPath"
                              >
                                <div class="default-btn big-watched">
                                  <i class="ion-plus"></i> Mark as watched
                                </div>
                                <div class="default-btn sm-watched">
                                  <i class="ion-plus"></i> Set as watched
                                </div>
                              </a>
                            </template>
                            <template v-if="isAuthenticated">
                              <button
                                class="fs-ep-btn ep-right-btn"
                                :class="{ 'btn--favourited': isSeasonEpisodeFavourited(episode) }"
                                data-testid="season-episode-favourite-action"
                                type="button"
                                :disabled="seasonEpisodeFavouritePending[episodeInteractionCode(episode)]"
                                @click="toggleSeasonEpisodeFavourite(episode)"
                              >
                                <div class="default-btn big-fav">
                                  <i class="ion-heart"></i> {{ seasonEpisodeFavouriteButtonText(episode) }}
                                </div>
                                <div class="default-btn sm-fav">
                                  <i class="ion-heart"></i> {{ seasonEpisodeFavouriteButtonText(episode, true) }}
                                </div>
                              </button>
                            </template>
                            <template v-else>
                              <a
                                class="fs-ep-btn ep-right-btn"
                                data-testid="season-episode-favourite-action"
                                :href="loginPath"
                              >
                                <div class="default-btn big-fav">
                                  <i class="ion-heart"></i> Add to favourites
                                </div>
                                <div class="default-btn sm-fav">
                                  <i class="ion-heart"></i> Favourite
                                </div>
                              </a>
                            </template>
                          </div>
                        </div>
                        <div class="row g-0 ep-rating-row" @mouseleave="resetSeasonEpisodeRatingPreview(episode)">
                          <div class="col-8 g-0 ep-ratings-col">
                            <div class="ep-rating-stars">
                              <template v-if="isAuthenticated">
                                <button
                                  v-for="star in 10"
                                  :key="`desktop-${index}-${star}`"
                                  data-testid="season-episode-rating-star"
                                  type="button"
                                  @mouseenter="previewSeasonEpisodeRating(episode, star)"
                                  @click="setSeasonEpisodeRating(episode, star)"
                                >
                                  <i :class="seasonEpisodeRatingStarClass(episode, star)"></i>
                                </button>
                              </template>
                              <template v-else>
                                <a
                                  v-for="star in 10"
                                  :key="`desktop-${index}-${star}`"
                                  data-testid="season-episode-rating-star"
                                  :href="loginPath"
                                >
                                  <i class="rating-star ion-ios-star-outline"></i>
                                </a>
                              </template>
                            </div>
                          </div>
                          <div class="col-2 g-0 current-ep-rating">
                            <p class="current-ep-rating-text" data-testid="season-episode-rating-text">
                              {{ seasonEpisodeRatingText(episode) }}
                            </p>
                          </div>
                        </div>
                        <div
                          v-if="episode.overviewText"
                          class="row g-0 episode-overview-row"
                          data-testid="season-episode-overview"
                        >
                          <div class="col g-0">
                            <p class="episode-overview-text">
                              <span data-testid="season-episode-overview-body">{{ displayedSeasonEpisodeOverview(episode) }}</span>
                              <button
                                v-if="hasExpandableSeasonEpisodeOverview(episode)"
                                class="overview-toggle"
                                data-testid="season-episode-overview-toggle"
                                type="button"
                                :aria-label="
                                  isSeasonEpisodeOverviewExpanded(episode)
                                    ? 'Show less episode overview'
                                    : 'Show full episode overview'
                                "
                                @click="toggleSeasonEpisodeOverview(episode)"
                              >
                                <span class="overview-toggle__icon" aria-hidden="true">
                                  {{ isSeasonEpisodeOverviewExpanded(episode) ? '▲' : '▼' }}
                                </span>
                              </button>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="episode.overviewText"
                    :class="[
                      'row',
                      'g-0',
                      'episode-overview-tablet-row',
                      index === episodes.length - 1 ? 'last-episode-overview-row' : null,
                    ]"
                    data-testid="season-episode-tablet-overview"
                  >
                    <div class="col g-0">
                      <p class="episode-overview-text">
                        <span data-testid="season-episode-overview-body">{{ displayedSeasonEpisodeOverview(episode) }}</span>
                        <button
                          v-if="hasExpandableSeasonEpisodeOverview(episode)"
                          class="overview-toggle"
                          data-testid="season-episode-overview-toggle"
                          type="button"
                          :aria-label="
                            isSeasonEpisodeOverviewExpanded(episode)
                              ? 'Show less episode overview'
                              : 'Show full episode overview'
                          "
                          @click="toggleSeasonEpisodeOverview(episode)"
                        >
                          <span class="overview-toggle__icon" aria-hidden="true">
                            {{ isSeasonEpisodeOverviewExpanded(episode) ? '▲' : '▼' }}
                          </span>
                        </button>
                      </p>
                    </div>
                  </div>
                  <div :class="['row', 'g-0', 'episode-container-sm', index === 0 ? 'first-sm-ep' : null]">
                    <div class="col g-0">
                      <div class="ep-image-col">
                        <a :href="episode.href || '#'">
                          <img class="ep-image" :src="episode.imageSrc" :alt="`${showTitle} ${seasonTitle} ${episode.numberText} image`">
                        </a>
                        <p class="ep-aired-sm">{{ episode.metaText }}</p>
                      </div>
                      <div class="ep-title-container-sm">
                        <h3 class="ep-title"><a :href="episode.href || '#'">{{ episode.numberText }}</a></h3>
                      </div>
                      <div class="ep-buttons">
                        <template v-if="isAuthenticated">
                          <button
                            class="fs-ep-btn ep-left-btn"
                            :class="{ 'btn--watched': isSeasonEpisodeWatched(episode) }"
                            type="button"
                            :disabled="seasonEpisodeWatchPending[episodeInteractionCode(episode)]"
                            @click="markSeasonEpisodeWatched(episode)"
                          >
                            <div class="default-btn big-watched">
                              <i :class="seasonEpisodeWatchIconClass(episode)"></i> {{ seasonEpisodeWatchButtonText(episode) }}
                            </div>
                            <div class="default-btn sm-watched">
                              <i :class="seasonEpisodeWatchIconClass(episode)"></i> {{ seasonEpisodeWatchButtonText(episode, true) }}
                            </div>
                          </button>
                        </template>
                        <template v-else>
                          <a class="fs-ep-btn ep-left-btn" :href="loginPath">
                            <div class="default-btn big-watched">
                              <i class="ion-plus"></i> Mark as watched
                            </div>
                            <div class="default-btn sm-watched">
                              <i class="ion-plus"></i> Set as watched
                            </div>
                          </a>
                        </template>
                        <template v-if="isAuthenticated">
                          <button
                            class="fs-ep-btn ep-right-btn"
                            :class="{ 'btn--favourited': isSeasonEpisodeFavourited(episode) }"
                            type="button"
                            :disabled="seasonEpisodeFavouritePending[episodeInteractionCode(episode)]"
                            @click="toggleSeasonEpisodeFavourite(episode)"
                          >
                            <div class="default-btn big-fav">
                              <i class="ion-heart"></i> {{ seasonEpisodeFavouriteButtonText(episode) }}
                            </div>
                            <div class="default-btn sm-fav">
                              <i class="ion-heart"></i> {{ seasonEpisodeFavouriteButtonText(episode, true) }}
                            </div>
                          </button>
                        </template>
                        <template v-else>
                          <a class="fs-ep-btn ep-right-btn" :href="loginPath">
                            <div class="default-btn big-fav">
                              <i class="ion-heart"></i> Add to favourites
                            </div>
                            <div class="default-btn sm-fav">
                              <i class="ion-heart"></i> Favourite
                            </div>
                          </a>
                        </template>
                      </div>
                      <div class="row g-0 ep-rating-row" @mouseleave="resetSeasonEpisodeRatingPreview(episode)">
                        <div class="col-8 g-0 ep-ratings-col">
                          <div class="ep-rating-stars">
                            <template v-if="isAuthenticated">
                              <button
                                v-for="star in 10"
                                :key="`mobile-${index}-${star}`"
                                type="button"
                                @mouseenter="previewSeasonEpisodeRating(episode, star)"
                                @click="setSeasonEpisodeRating(episode, star)"
                              >
                                <i :class="seasonEpisodeRatingStarClass(episode, star)"></i>
                              </button>
                            </template>
                            <template v-else>
                              <a v-for="star in 10" :key="`mobile-${index}-${star}`" :href="loginPath">
                                <i class="rating-star ion-ios-star-outline"></i>
                              </a>
                            </template>
                          </div>
                        </div>
                        <div class="col-2 g-0 current-ep-rating">
                          <p class="current-ep-rating-text">{{ seasonEpisodeRatingText(episode) }}</p>
                        </div>
                      </div>
                      <div
                        v-if="episode.overviewText"
                        class="episode-overview-sm"
                        data-testid="season-episode-overview"
                      >
                        <p>
                          <span data-testid="season-episode-overview-body">{{ displayedSeasonEpisodeOverview(episode) }}</span>
                          <button
                            v-if="hasExpandableSeasonEpisodeOverview(episode)"
                            class="overview-toggle"
                            data-testid="season-episode-overview-toggle"
                            type="button"
                            :aria-label="
                              isSeasonEpisodeOverviewExpanded(episode)
                                ? 'Show less episode overview'
                                : 'Show full episode overview'
                            "
                            @click="toggleSeasonEpisodeOverview(episode)"
                          >
                            <span class="overview-toggle__icon" aria-hidden="true">
                              {{ isSeasonEpisodeOverviewExpanded(episode) ? '▲' : '▼' }}
                            </span>
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </template>

            <div v-else-if="seasonLoaded" class="row g-0">
              <div class="col g-0">
                <p class="season-episodes-empty">No episodes available, yet.</p>
              </div>
            </div>
            <div v-else class="row g-0">
              <div class="col g-0">
                <p class="season-episodes-empty">Loading episodes...</p>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-3 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'credits'" class="container g-0" data-testid="season-cast-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="season-cast-heading">Cast</h2>
              </div>
            </div>
            <div v-if="seasonLoaded" class="row g-0 credits-empty-row">
              <div class="col g-0">
                <p class="no-cast-message no-credits-message" data-testid="season-no-cast-message">
                  No cast available
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'you'" class="container g-0" data-testid="season-you-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-8 col-lg-8 you-watch-content" data-testid="season-you-content">
          <div class="container g-0">
            <div class="row g-0 credits-title">
              <div class="col g-0">
                <h2 class="sub-heading" data-testid="season-you-heading">{{ seasonYouHeading }}</h2>
              </div>
            </div>
            <p v-if="seasonYouWatchesError" class="film-you-empty" data-testid="season-you-error">
              {{ seasonYouWatchesError }}
            </p>
            <p v-else-if="seasonYouWatchesLoading" class="film-you-empty" data-testid="season-you-loading">
              Loading watches...
            </p>
            <p
              v-else-if="seasonYouWatchesLoaded && seasonYouWatches.length === 0"
              class="film-you-empty"
              data-testid="season-you-empty"
            >
              No watches yet.
            </p>
            <div v-else class="film-you-watch-list" data-testid="season-you-watch-list">
              <template v-for="entry in seasonYouWatchEntries" :key="entry.key">
                <h3
                  v-if="entry.type === 'year'"
                  :class="[
                    'season-you-watch-year-header',
                    entry.hasMonthHeader ? null : 'season-you-watch-year-header--no-month',
                  ]"
                  data-testid="season-you-watch-year-header"
                >
                  {{ entry.label }}
                </h3>
                <h4
                  v-else-if="entry.type === 'month'"
                  class="season-you-watch-month-header"
                  data-testid="season-you-watch-month-header"
                >
                  {{ entry.label }}
                </h4>
                <div
                  v-else
                  :class="[
                    'film-you-watch-row',
                    entry.hasBorder ? 'film-you-watch-row--bordered' : null,
                  ]"
                  data-testid="season-you-watch-row"
                >
                <div class="film-you-watch-date-column">
                  <p class="film-you-watch-date" data-testid="season-you-watch-date">
                    {{ seasonYouWatchDateText(entry.watch) }}
                  </p>
                  <p
                    class="film-you-watch-description season-you-watch-episode season-you-watch-episode--mobile"
                    data-testid="season-you-watch-episode"
                  >
                    <a :href="entry.watch.episode.href">
                      {{ seasonYouWatchEpisodeLabel(entry.watch) }}
                    </a>
                  </p>
                </div>
                <div class="season-you-watch-episode-column" data-testid="season-you-watch-episode-column">
                  <a :href="entry.watch.episode.href">
                    {{ seasonYouWatchEpisodeLabel(entry.watch) }}
                  </a>
                </div>
                <p
                  v-if="entry.watch.description"
                  class="film-you-watch-description season-you-watch-description-mobile"
                  data-testid="season-you-watch-description-mobile"
                >
                  "{{ entry.watch.description }}"
                </p>
                <p
                  v-if="seasonYouWatchFormatText(entry.watch)"
                  class="film-you-watch-description season-you-watch-format-mobile"
                  data-testid="season-you-watch-format-mobile"
                >
                  {{ seasonYouWatchFormatText(entry.watch) }}
                </p>
                <div class="film-you-watch-format" data-testid="season-you-watch-format">
                  {{ seasonYouWatchFormatText(entry.watch) }}
                </div>
                <p
                  v-if="entry.watch.description"
                  class="film-you-watch-description season-you-watch-description--desktop"
                  data-testid="season-you-watch-description"
                >
                  "{{ entry.watch.description }}"
                </p>
                <div class="film-you-watch-actions">
                  <button
                    class="film-you-watch-action"
                    data-testid="season-you-watch-edit"
                    type="button"
                    aria-label="Edit watch"
                    @click="openSeasonWatchEditModal(entry.watch)"
                  >
                    <i class="ion-edit"></i>
                  </button>
                  <button
                    class="film-you-watch-action"
                    data-testid="season-you-watch-remove"
                    type="button"
                    aria-label="Remove watch"
                    @click="removeSeasonYouWatch(entry.watch)"
                  >
                    <i class="ion-ios-close-outline"></i>
                  </button>
                </div>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="activeSection === 'comments'" class="container g-0" data-testid="comments-panel">
      <div class="row g-0 film-overview">
        <div class="col-12 col-sm-12 col-md-12 col-lg-10">
          <CommentsPanel
            :base-path="basePath"
            heading="Discussion"
            :is-active="activeSection === 'comments'"
            target-type="season"
            :target-id="season.id"
            test-id-prefix="season"
          />
        </div>

        <div class="d-none d-md-block col-lg-2 ad-section">
        </div>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-if="seasonWatchModalOpen"
        class="film-watch-modal-overlay"
        data-testid="season-watch-modal-overlay"
      >
        <button
          class="film-watch-modal-backdrop"
          type="button"
          aria-label="Close watch modal"
          :disabled="seasonWatchModalPending"
          @click="closeSeasonWatchModal"
        ></button>
        <div
          class="film-watch-modal season-watch-modal"
          data-testid="season-watch-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="season-watch-modal-title"
        >
          <div class="film-watch-modal__header">
            <div class="episode-watch-modal__title-wrap">
              <h2 id="season-watch-modal-title" class="film-watch-modal__title">
                {{ seasonWatchModalTitle }}
              </h2>
              <p class="episode-watch-modal__subtitle">{{ seasonWatchModalSubtitle }}</p>
            </div>
            <button
              class="film-watch-modal__close"
              data-testid="season-watch-close"
              type="button"
              aria-label="Close watch modal"
              :disabled="seasonWatchModalPending"
              @click="closeSeasonWatchModal"
            >
              <svg
                class="film-watch-modal__close-icon"
                viewBox="0 0 20 20"
                aria-hidden="true"
              >
                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
                <path
                  d="M7 7 L13 13 M13 7 L7 13"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.4"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <p
            v-if="seasonWatchModalError"
            class="film-watch-modal__error"
            data-testid="season-watch-error"
          >
            {{ seasonWatchModalError }}
          </p>

          <form class="film-watch-modal__form" @submit.prevent="saveSeasonWatch">
            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Watch date</h3>
                <p>Optional</p>
              </div>
              <div class="film-watch-modal__date-grid">
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': seasonWatchDatePickerOpen === 'year' }"
                >
                  <span>Year</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': seasonWatchDatePickerOpen === 'year' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': seasonWatchDatePickerOpen === 'year' }"
                      data-testid="season-watch-year-picker"
                      type="button"
                      :disabled="seasonWatchModalPending"
                      @click="toggleSeasonWatchDatePicker('year')"
                    >
                      <span class="film-list-picker__label">{{ selectedSeasonWatchYearLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="seasonWatchDatePickerOpen === 'year'"
                      class="film-list-picker__menu"
                      data-testid="season-watch-year-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !seasonWatchYear }"
                        data-testid="season-watch-year-option-empty"
                        type="button"
                        @click="selectSeasonWatchYear('')"
                      >
                        Select year
                      </button>
                      <button
                        v-for="year in seasonWatchYearOptions"
                        :key="year"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': year === seasonWatchYear }"
                        :data-testid="`season-watch-year-option-${year}`"
                        type="button"
                        @click="selectSeasonWatchYear(year)"
                      >
                        {{ year }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="seasonWatchYear"
                    data-testid="season-watch-year"
                    :disabled="seasonWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select year</option>
                    <option v-for="year in seasonWatchYearOptions" :key="year" :value="year">
                      {{ year }}
                    </option>
                  </select>
                </label>
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': seasonWatchDatePickerOpen === 'month' }"
                >
                  <span>Month</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': seasonWatchDatePickerOpen === 'month' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': seasonWatchDatePickerOpen === 'month' }"
                      data-testid="season-watch-month-picker"
                      type="button"
                      :disabled="seasonWatchModalPending"
                      @click="toggleSeasonWatchDatePicker('month')"
                    >
                      <span class="film-list-picker__label">{{ selectedSeasonWatchMonthLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="seasonWatchDatePickerOpen === 'month'"
                      class="film-list-picker__menu"
                      data-testid="season-watch-month-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !seasonWatchMonth }"
                        data-testid="season-watch-month-option-empty"
                        type="button"
                        @click="selectSeasonWatchMonth('')"
                      >
                        Select month
                      </button>
                      <button
                        v-for="month in seasonWatchMonthOptions"
                        :key="month.value"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': month.value === seasonWatchMonth }"
                        :data-testid="`season-watch-month-option-${month.value}`"
                        type="button"
                        @click="selectSeasonWatchMonth(month.value)"
                      >
                        {{ month.label }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="seasonWatchMonth"
                    data-testid="season-watch-month"
                    :disabled="seasonWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select month</option>
                    <option v-for="month in seasonWatchMonthOptions" :key="month.value" :value="month.value">
                      {{ month.label }}
                    </option>
                  </select>
                </label>
                <label
                  class="film-watch-modal__field"
                  :class="{ 'film-watch-modal__field--picker-open': seasonWatchDatePickerOpen === 'day' }"
                >
                  <span>Day</span>
                  <div
                    class="film-list-picker"
                    :class="{ 'film-list-picker--open': seasonWatchDatePickerOpen === 'day' }"
                  >
                    <button
                      class="film-list-picker__toggle"
                      :class="{ 'film-list-picker__toggle--open': seasonWatchDatePickerOpen === 'day' }"
                      data-testid="season-watch-day-picker"
                      type="button"
                      :disabled="seasonWatchModalPending"
                      @click="toggleSeasonWatchDatePicker('day')"
                    >
                      <span class="film-list-picker__label">{{ selectedSeasonWatchDayLabel }}</span>
                      <span class="film-list-picker__icon ion-chevron-down" aria-hidden="true"></span>
                    </button>
                    <div
                      v-if="seasonWatchDatePickerOpen === 'day'"
                      class="film-list-picker__menu"
                      data-testid="season-watch-day-menu"
                    >
                      <button
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': !seasonWatchDay }"
                        data-testid="season-watch-day-option-empty"
                        type="button"
                        @click="selectSeasonWatchDay('')"
                      >
                        Select day
                      </button>
                      <button
                        v-for="day in seasonWatchDayOptions"
                        :key="day"
                        class="film-list-picker__option"
                        :class="{ 'film-list-picker__option--selected': day === seasonWatchDay }"
                        :data-testid="`season-watch-day-option-${day}`"
                        type="button"
                        @click="selectSeasonWatchDay(day)"
                      >
                        {{ day }}
                      </button>
                    </div>
                  </div>
                  <select
                    v-model="seasonWatchDay"
                    data-testid="season-watch-day"
                    :disabled="seasonWatchModalPending"
                    class="film-list-picker__native"
                    tabindex="-1"
                    aria-hidden="true"
                  >
                    <option value="">Select day</option>
                    <option v-for="day in seasonWatchDayOptions" :key="day" :value="day">
                      {{ day }}
                    </option>
                  </select>
                </label>
                <button
                  class="film-watch-modal__today"
                  data-testid="season-watch-today"
                  type="button"
                  :disabled="seasonWatchModalPending"
                  @click="setSeasonWatchToday"
                >
                  Today
                </button>
              </div>
            </section>

            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Episode format</h3>
                <p>Optional</p>
              </div>
              <div class="film-watch-modal__format-groups">
                <div
                  v-for="(formatGroup, groupIndex) in seasonWatchFormatGroups"
                  :key="`season-watch-format-group-${groupIndex}`"
                  class="film-watch-modal__format-group"
                  :class="{ 'film-watch-modal__format-group--two-col': formatGroup.columns === 2 }"
                >
                  <label
                    v-for="formatOption in formatGroup.items"
                    :key="formatOption.value"
                    class="film-watch-modal__choice"
                  >
                    <input
                      v-model="seasonWatchFormat"
                      :data-testid="`season-watch-format-${formatOption.value}`"
                      type="radio"
                      name="season-watch-format"
                      :value="formatOption.value"
                      :disabled="seasonWatchModalPending"
                    >
                    <span>{{ formatOption.label }}</span>
                  </label>
                </div>
              </div>
            </section>

            <section class="film-watch-modal__section">
              <div class="film-watch-modal__section-header">
                <h3>Notes to self</h3>
                <p>Optional</p>
              </div>
              <textarea
                v-model="seasonWatchDescription"
                class="film-watch-modal__textarea"
                data-testid="season-watch-description"
                maxlength="300"
                rows="5"
                :disabled="seasonWatchModalPending"
              ></textarea>
              <div class="film-watch-modal__notes-footer">
                <p class="film-watch-modal__counter">
                  {{ seasonWatchRemainingCharacters }} characters left.
                </p>
                <label class="film-watch-modal__checkbox film-watch-modal__notes-public">
                  <input
                    v-model="seasonWatchPublicDescription"
                    data-testid="season-watch-public"
                    type="checkbox"
                    :disabled="seasonWatchModalPending"
                  >
                  <span>Public</span>
                </label>
              </div>
            </section>

            <div class="film-watch-modal__actions film-watch-modal__actions--end">
              <div class="film-watch-modal__action-group">
                <button
                  class="film-watch-modal__action film-watch-modal__action--secondary"
                  type="button"
                  :disabled="seasonWatchModalPending"
                  @click="closeSeasonWatchModal"
                >
                  Cancel
                </button>
                <button
                  class="film-watch-modal__action"
                  data-testid="season-watch-save"
                  type="submit"
                  :disabled="seasonWatchModalPending"
                >
                  Save
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Teleport>
    <Teleport to="body">
      <div
        v-if="compactSeasonEpisodeRatingPickerEpisode"
        class="season-compact-rating-picker-overlay"
        data-testid="season-compact-rating-picker-overlay"
        @click.self="closeCompactSeasonEpisodeRatingPicker"
      >
        <div class="season-compact-rating-picker" data-testid="season-compact-rating-picker">
          <button
            class="season-compact-rating-picker__close"
            data-testid="season-compact-rating-picker-close"
            type="button"
            aria-label="Close rating picker"
            @click="closeCompactSeasonEpisodeRatingPicker"
          >
            <svg
              class="season-compact-rating-picker__close-icon"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" stroke-width="1.4" />
              <path
                d="M7 7 L13 13 M13 7 L7 13"
                fill="none"
                stroke="currentColor"
                stroke-width="1.4"
                stroke-linecap="round"
              />
            </svg>
          </button>
          <p class="season-compact-rating-picker__title">
            {{ compactSeasonEpisodeRatingPickerEpisode.numberOnly }} • {{ compactSeasonEpisodeRatingPickerEpisode.titleOnly }}
          </p>
          <div class="season-compact-rating-picker__stars">
            <button
              v-for="star in 10"
              :key="`compact-season-rating-${episodeInteractionCode(compactSeasonEpisodeRatingPickerEpisode)}-${star}`"
              class="season-compact-rating-picker__star"
              data-testid="season-compact-rating-picker-star"
              type="button"
              :disabled="seasonEpisodeRatingPending[episodeInteractionCode(compactSeasonEpisodeRatingPickerEpisode)]"
              @mouseenter="previewSeasonEpisodeRating(compactSeasonEpisodeRatingPickerEpisode, star)"
              @click="selectCompactSeasonEpisodeRating(star)"
            >
              <i :class="seasonEpisodeRatingStarClass(compactSeasonEpisodeRatingPickerEpisode, star)"></i>
            </button>
          </div>
          <button
            v-if="seasonEpisodeRatingValue(compactSeasonEpisodeRatingPickerEpisode)"
            class="season-compact-rating-picker__reset"
            data-testid="season-compact-rating-picker-reset"
            type="button"
            :disabled="seasonEpisodeRatingPending[episodeInteractionCode(compactSeasonEpisodeRatingPickerEpisode)]"
            @click="removeCompactSeasonEpisodeRating"
          >
            Reset rating
          </button>
        </div>
      </div>
    </Teleport>

    <div
      v-if="seasonBulkWatchModalOpen"
      class="season-bulk-watch-modal"
      data-testid="season-bulk-watch-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="season-bulk-watch-modal-title"
    >
      <button
        class="season-bulk-watch-modal__backdrop"
        type="button"
        aria-label="Close season bulk watch status"
        @click="closeSeasonBulkWatchModal"
      ></button>
      <div class="season-bulk-watch-modal__card">
        <p id="season-bulk-watch-modal-title" class="season-bulk-watch-modal__title">
          Season marked as watched
        </p>
        <p class="season-bulk-watch-modal__body" data-testid="season-bulk-watch-modal-body">
          {{ seasonBulkWatchModalBody }}
        </p>
        <div
          class="season-bulk-watch-modal__progress season-bulk-watch-modal__progress--complete"
          aria-hidden="true"
        >
          <span class="season-bulk-watch-modal__progress-bar"></span>
        </div>
        <div class="season-bulk-watch-modal__actions">
          <button
            class="season-bulk-watch-modal__close"
            data-testid="season-bulk-watch-close"
            type="button"
            @click="closeSeasonBulkWatchModal"
          >
            Close
          </button>
        </div>
      </div>
    </div>

    <MobileSearchOverlay :open="mobileSearchOpen" @close="closeMobileSearchOverlay" />
  </main>
</template>

<script setup>
import { usePageViewTracking } from '../analytics/pageViews'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/single-season/season-detail.css'
import CommentsPanel from '../components/CommentsPanel.vue'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import ViewModeIcon from '../components/ViewModeIcon'
import NotFoundView from './NotFoundView.vue'
import { useAuthStore } from '../stores/auth'
import { buildLoginPath } from '../utils/auth'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { buildMetaDescription, useMetaDescription } from '../utils/metaDescription'
import { clipOverviewWithSuffix, hasExpandableOverview } from '../utils/overview'

const route = useRoute()
const capturePageView = usePageViewTracking()
const auth = useAuthStore()
auth.initFromStorage()
const slug = computed(() => route.params.slug || '')
const rawSeasonNumber = computed(() => route.params.seasonNumber || '')
const seasonNumber = computed(() => normalizeSeasonNumberParam(rawSeasonNumber.value))
const seasonRouteSegment = computed(() => String(rawSeasonNumber.value || seasonNumber.value))
const parentShowPath = computed(() => `/shows/${slug.value}`)
const parentShowSeasonsPath = computed(() => `/shows/${slug.value}/seasons`)
const basePath = computed(() => `/shows/${slug.value}/season/${seasonRouteSegment.value}`)
const loginPath = computed(() => buildLoginPath(route.fullPath || basePath.value))
const isAuthenticated = computed(() => auth.isAuthenticated.value)
const authUsername = computed(() => auth.state.username || 'Account')
const activeSection = computed(() => {
  const section = route.params.section || 'overview'
  return section === 'discussion' ? 'comments' : section
})
const posterPlaceholderImage = '/static/films/img/placeholder-film-camera.svg'
const defaultBackgroundImage = '/media/films/backgrounds/default-bg.jpg'
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const monthOptionLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const EPISODE_OVERVIEW_CLIP_LIMIT = 275
const seasonWatchMonthOptions = monthOptionLabels.map((label, index) => ({
  value: String(index + 1).padStart(2, '0'),
  label,
}))
const seasonWatchFormatGroups = [
  {
    columns: 1,
    items: [
      { value: 'streaming', label: 'Streaming' },
    ],
  },
  {
    columns: 2,
    items: [
      { value: 'blu-ray', label: 'Blu-ray' },
      { value: 'dvd', label: 'DVD' },
      { value: '4k-ultra-hd', label: '4K Ultra HD' },
      { value: 'hd-dvd', label: 'HD-DVD' },
      { value: 'vhs', label: 'VHS' },
    ],
  },
  {
    columns: 2,
    items: [
      { value: 'tv', label: 'TV broadcast' },
      { value: 'digital', label: 'Digital copy' },
    ],
  },
]

const assets = {
  logo: '/static/spa/branding/logo.png',
}

const listingButtons = [
  { key: '1', label: 'Detailed view' },
  { key: '2', label: 'Poster grid view' },
  { key: '3', label: 'Compact list view' },
]
const listingModeStorageKey = 'filmsomniac:seasonEpisodeListingMode'
const mobileSearchOpen = ref(false)

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

function createEmptySeasonState() {
  return {
    id: null,
    title: '',
    startYear: null,
    endYear: null,
    seasonTitle: '',
    seasonYear: '',
    posterUrl: '',
    backgroundUrl: '',
    mobileBackgroundUrl: '',
    overview: '',
    episodeCountText: 'Episodes (0)',
    prevSeason: null,
    nextSeason: null,
    episodes: [],
  }
}

const seasonLoaded = ref(false)
const seasonNotFound = ref(false)
const anonymousYouSection = computed(() => activeSection.value === 'you' && !isAuthenticated.value)
const showSeasonNotFound = computed(() => seasonNotFound.value || anonymousYouSection.value)
const seasonNotFoundHeading = computed(() =>
  anonymousYouSection.value ? 'Page was not found' : 'Season not found',
)
const seasonNotFoundMessage = computed(() =>
  anonymousYouSection.value
    ? 'The page you were looking for could not be found.'
    : 'The season you were looking for could not be found.',
)
const seasonFavouritePending = ref(false)
const seasonBulkWatchPending = ref(false)
const seasonBulkWatchModalOpen = ref(false)
const seasonBulkWatchCompletedEpisodeCount = ref(0)
const seasonFavourited = ref(false)
const seasonDetailIncludesSeasonFavouriteState = ref(false)
const seasonEpisodeWatchState = ref({})
const seasonEpisodeWatchPending = ref({})
const seasonEpisodeInteractionsLoaded = ref(false)
const seasonEpisodeFavouriteState = ref({})
const seasonEpisodeFavouritePending = ref({})
const seasonEpisodeRatingState = ref({})
const seasonEpisodeRatingPending = ref({})
const seasonEpisodeHoverRatings = ref({})
const expandedSeasonEpisodeOverviewIds = ref({})
const compactSeasonEpisodeRatingPickerEpisode = ref(null)
const activeListingMode = ref(getStoredListingMode())
const useCompactListMode = ref(false)
const seasonYouWatches = ref([])
const seasonYouWatchesLoading = ref(false)
const seasonYouWatchesLoaded = ref(false)
const seasonYouWatchesError = ref('')
const seasonWatchModalOpen = ref(false)
const seasonWatchModalPending = ref(false)
const seasonWatchModalError = ref('')
const seasonWatchEditing = ref(null)
const seasonWatchYear = ref('')
const seasonWatchMonth = ref('')
const seasonWatchDay = ref('')
const seasonWatchDatePickerOpen = ref('')
const seasonWatchFormat = ref('')
const seasonWatchDescription = ref('')
const seasonWatchPublicDescription = ref(false)
const season = ref(createEmptySeasonState())
const seasonPosterStatus = ref('loading')

const showTitle = computed(() => season.value.title || '')
const seasonTitle = computed(() => season.value.seasonTitle || '')
const seasonDisplayLabel = computed(() => (
  String(seasonNumber.value) === '0' ? 'Specials' : `Season ${seasonNumber.value}`
))
const documentTitle = computed(() => buildDocumentTitle([
  showTitle.value,
  seasonDisplayLabel.value,
], activeSection.value))
const seasonYearDisplay = computed(() => season.value.seasonYear || '')
const hasLoadedSeasonPayload = computed(() => seasonLoaded.value && !showSeasonNotFound.value)
const backgroundUrl = computed(() => season.value.backgroundUrl || '')
const mobileBackgroundUrl = computed(() => season.value.mobileBackgroundUrl || '')
const seasonPosterLoading = computed(() => seasonPosterStatus.value === 'loading')
const seasonPosterImageSrc = computed(() => season.value.posterUrl || '')
const seasonPosterFrameClasses = computed(() => [
  'image-load-frame',
  'image-load-frame--poster',
  seasonPosterStatus.value === 'loading' ? 'image-load-frame--loading' : null,
  seasonPosterStatus.value === 'loaded' ? 'image-load-frame--loaded' : null,
  seasonPosterStatus.value === 'fallback' ? 'image-load-frame--fallback' : null,
])
const seasonPosterImageClasses = computed(() => [
  'hero__poster',
  'image-load-frame__image',
  seasonPosterStatus.value !== 'loading' ? 'image-load-frame__image--visible' : null,
])
const overview = computed(() => season.value.overview || '')
const episodeCountText = computed(() => season.value.episodeCountText || 'Episodes (0)')
const prevSeason = computed(() => withPreservedSeasonSection(season.value.prevSeason))
const nextSeason = computed(() => withPreservedSeasonSection(season.value.nextSeason))
const isSeasonFavourited = computed(() => seasonFavourited.value)
const favouriteButtonText = computed(() => (isSeasonFavourited.value ? 'Favourited' : 'Add to favourites'))
const favouriteButtonShortText = computed(() => (isSeasonFavourited.value ? 'Favourited' : 'Favourite'))
const episodes = computed(() => season.value.episodes || [])
const seasonYouWatchCount = computed(() => seasonYouWatches.value.length)
const seasonYouHeading = computed(() => `Watches (${seasonYouWatchCount.value})`)
const seasonYouWatchEntries = computed(() => buildSeasonYouWatchEntries(seasonYouWatches.value))
const seasonWatchModalTitle = computed(() => showTitle.value || 'Edit watch')
const seasonWatchModalSubtitle = computed(() => {
  const watch = seasonWatchEditing.value
  const episode = watch?.episode || {}
  const episodeLabel = [episode.seasonEpisodeNumber, episode.title].filter(Boolean).join(' ')
  return episodeLabel || 'Edit watch'
})
const seasonWatchCurrentUtcYear = new Date().getUTCFullYear()
const seasonWatchYearOptions = Array.from(
  { length: seasonWatchCurrentUtcYear - 1899 },
  (_, index) => String(seasonWatchCurrentUtcYear - index),
)
const selectedSeasonWatchYearLabel = computed(() => seasonWatchYear.value || 'Select year')
const selectedSeasonWatchMonthLabel = computed(() => (
  seasonWatchMonthOptions.find((month) => month.value === seasonWatchMonth.value)?.label || 'Select month'
))
const selectedSeasonWatchDayLabel = computed(() => seasonWatchDay.value || 'Select day')
const seasonWatchRemainingCharacters = computed(() => Math.max(0, 300 - seasonWatchDescription.value.length))
const seasonWatchSelectedDayCount = computed(() => {
  if (!seasonWatchYear.value || !seasonWatchMonth.value) {
    return 31
  }
  return new Date(
    Date.UTC(
      Number.parseInt(seasonWatchYear.value, 10),
      Number.parseInt(seasonWatchMonth.value, 10),
      0,
    ),
  ).getUTCDate()
})
const seasonWatchDayOptions = computed(() =>
  Array.from({ length: seasonWatchSelectedDayCount.value }, (_, index) =>
    String(index + 1).padStart(2, '0'),
  ),
)
const seasonWatchableEpisodes = computed(() => episodes.value.filter((episode) => episodeInteractionCode(episode)))
const seasonWatchedEpisodeCount = computed(
  () => seasonWatchableEpisodes.value.filter((episode) => isSeasonEpisodeWatched(episode)).length,
)
const seasonTotalEpisodeCount = computed(() => seasonWatchableEpisodes.value.length)
const seasonBulkWatchActionVisible = computed(() => {
  if (seasonTotalEpisodeCount.value === 0) return false
  if (!isAuthenticated.value) return true
  if (!seasonEpisodeInteractionsLoaded.value) return false
  return seasonWatchedEpisodeCount.value === 0
})
const seasonBulkWatchButtonText = computed(() => 'Mark season as watched')
const seasonBulkWatchModalBody = computed(() => {
  const episodeCount = seasonBulkWatchCompletedEpisodeCount.value || seasonTotalEpisodeCount.value
  const episodeLabel = episodeCount === 1 ? 'episode' : 'episodes'
  return `All ${episodeCount} ${episodeLabel} of ${seasonDisplayLabel.value} were marked as watched.`
})
const heroBackgroundStyle = computed(() => (
  backgroundUrl.value
    ? { backgroundImage: `url(${backgroundUrl.value})` }
    : undefined
))
const showYearDisplay = computed(() => formatShowYears(season.value))
const titleLengthClass = computed(() => {
  const titleLength = showTitle.value.length
  if (titleLength > 150) return 'item-title--extreme'
  if (titleLength > 120) return 'item-title--very-long'
  if (titleLength > 75) return 'item-title--long'
  return null
})

function resetSeasonPosterLoading(src = season.value.posterUrl) {
  if (!src) {
    seasonPosterStatus.value = 'loading'
    return
  }
  seasonPosterStatus.value = src === posterPlaceholderImage ? 'fallback' : 'loading'
}

function handleSeasonPosterLoad() {
  seasonPosterStatus.value = season.value.posterUrl === posterPlaceholderImage
    ? 'fallback'
    : 'loaded'
}

function handleSeasonPosterError() {
  if (season.value.posterUrl !== posterPlaceholderImage) {
    season.value = {
      ...season.value,
      posterUrl: posterPlaceholderImage,
    }
    return
  }
  seasonPosterStatus.value = 'fallback'
}

function formatShowYears(showEntry) {
  const startYear = showEntry.startYear
  const endYear = showEntry.endYear

  if (!startYear) return ''
  if (!endYear) return `(${startYear} - )`
  if (startYear === endYear) return `(${startYear})`
  return `(${startYear} - ${endYear})`
}

function normalizeEpisodeMeta(episode) {
  const parts = [episode.airdate_text || '', episode.runtime_text || ''].filter(Boolean)
  return parts.join(' • ')
}

function splitEpisodeHeading(numberText) {
  const match = String(numberText || '').match(/^(\d+)\s+(.+)$/)

  if (!match) {
    return {
      numberOnly: String(numberText || ''),
      titleOnly: String(numberText || ''),
    }
  }

  return {
    numberOnly: match[1],
    titleOnly: match[2],
  }
}

function normalizeEpisodes(episodeEntries) {
  if (!Array.isArray(episodeEntries)) return []

  return episodeEntries
    .filter((episode) => episode?.number_text)
    .map((episode) => {
      const heading = splitEpisodeHeading(episode.number_text)

      return {
        episodeId: episode.episode_id || null,
        episodeNumber: Number.parseInt(episode.episode_number, 10) || null,
        seasonEpisodeNumber: episode.season_episode_number || '',
        numberText: episode.number_text,
        numberOnly: heading.numberOnly,
        titleOnly: heading.titleOnly,
        overviewText: episode.overview_text || '',
        imageSrc: episode.image_src || '/static/films/img/placeholder-episode-tv.svg',
        metaText: normalizeEpisodeMeta(episode),
        href: episode.href || '#',
        favourited: Boolean(episode.favourited),
        watched: Boolean(episode.watched),
        rating: normalizeRatingValue(episode.rating),
        hasInteractionState:
          Object.prototype.hasOwnProperty.call(episode, 'favourited') ||
          Object.prototype.hasOwnProperty.call(episode, 'watched') ||
          Object.prototype.hasOwnProperty.call(episode, 'rating'),
      }
    })
}

function applySeasonDetailViewerState(payload, normalizedEpisodes) {
  const viewerState = payload?.viewer_state
  if (viewerState && typeof viewerState === 'object') {
    seasonFavourited.value = Boolean(viewerState.favourited)
    seasonDetailIncludesSeasonFavouriteState.value = true
  }

  const episodesWithState = Array.isArray(normalizedEpisodes)
    ? normalizedEpisodes.filter((episode) => episode.hasInteractionState)
    : []
  if (!episodesWithState.length) {
    return
  }

  const nextWatchState = {}
  const nextFavouriteState = {}
  const nextRatingState = {}

  episodesWithState.forEach((episode) => {
    const code = episodeInteractionCode(episode)
    if (!code) {
      return
    }
    nextWatchState[code] = Boolean(episode.watched)
    nextFavouriteState[code] = Boolean(episode.favourited)
    nextRatingState[code] = normalizeRatingValue(episode.rating)
  })

  seasonEpisodeWatchState.value = nextWatchState
  seasonEpisodeFavouriteState.value = nextFavouriteState
  seasonEpisodeRatingState.value = nextRatingState
  seasonEpisodeInteractionsLoaded.value = true
}

function normalizeSeasonYouWatch(payload) {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const episode = payload.episode || {}

  return {
    id: payload.id,
    watched: String(payload.watched || '').trim(),
    createdAt: String(payload.created_at || '').trim(),
    format: String(payload.format || '').trim(),
    formatLabel: String(payload.format_label || '').trim(),
    description: String(payload.description || '').trim(),
    publicDescription: Boolean(payload.public_description ?? payload.publicDescription),
    episode: {
      id: episode.id || null,
      number: Number.parseInt(episode.number, 10) || 0,
      seasonEpisodeNumber: String(episode.season_episode_number || episode.seasonEpisodeNumber || '').trim(),
      title: String(episode.title || '').trim(),
      href: String(episode.href || '#').trim() || '#',
    },
  }
}

function formatSeasonWatchDateValue(value) {
  const watchedValue = String(value || '').trim()
  const watchedMatch = watchedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!watchedMatch) {
    return ''
  }

  const [, year, month, day] = watchedMatch
  if (month === '00') {
    return year
  }

  const monthName = monthNames[Number(month) - 1]
  if (!monthName) {
    return year
  }

  if (day === '00') {
    return `${monthName}, ${year}`
  }

  return `${monthName} ${Number(day)}, ${year}`
}

function formatSeasonCreatedAtDateText(createdAt) {
  const value = String(createdAt || '').trim()
  if (!value) return ''

  const createdAtDate = new Date(value)
  if (Number.isNaN(createdAtDate.getTime())) return ''

  return `${monthNames[createdAtDate.getUTCMonth()]} ${createdAtDate.getUTCDate()}, ${createdAtDate.getUTCFullYear()}`
}

function seasonYouWatchDateText(watch) {
  const watchedText = formatSeasonWatchDateValue(watch?.watched)
  if (watchedText) {
    return watchedText
  }

  const createdAtText = formatSeasonCreatedAtDateText(watch?.createdAt)
  if (!createdAtText) {
    return 'No date given'
  }

  return `${createdAtText} (added)`
}

function withPreservedSeasonSection(targetSeason) {
  if (!targetSeason?.href) {
    return targetSeason || null
  }

  if (!['comments', 'you'].includes(activeSection.value)) {
    return targetSeason
  }

  const match = targetSeason.href.match(/^\/shows\/([^/]+)\/season\/([^/]+)(?:\/.*)?$/)
  if (!match || match[1] !== slug.value) {
    return targetSeason
  }

  return {
    ...targetSeason,
    href: `/shows/${match[1]}/season/${match[2]}/${activeSection.value}`,
  }
}

function normalizeSeasonNumberParam(value) {
  return String(value).toLowerCase() === 'specials' ? '0' : value
}

function seasonYouWatchFormatText(watch) {
  const label = watch?.formatLabel || watch?.format || ''
  return label === 'Streaming' ? 'Streamed' : label
}

function seasonYouWatchEpisodeLabel(watch) {
  return [seasonYouWatchEpisodeNumberText(watch), seasonYouWatchEpisodeTitleText(watch)]
    .filter(Boolean)
    .join(' ')
}

function seasonYouWatchEpisodeNumberText(watch) {
  const episode = watch?.episode || {}
  const episodeNumber = Number.parseInt(episode.number, 10)
  return Number.isInteger(episodeNumber) && episodeNumber > 0
    ? String(episodeNumber).padStart(2, '0')
    : String(episode.seasonEpisodeNumber || '').split('x').pop()
}

function seasonYouWatchEpisodeTitleText(watch) {
  return String(watch?.episode?.title || '').trim()
}

function seasonYouWatchSortValue(watch) {
  return String(watch?.watched || watch?.createdAt || '').trim()
}

function seasonYouWatchDateParts(watch) {
  const watchedValue = String(watch?.watched || '').trim()
  const watchedMatch = watchedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (watchedMatch) {
    const [, year, month] = watchedMatch
    return {
      year,
      month: month === '00' ? '' : month,
    }
  }

  const createdAtValue = String(watch?.createdAt || '').trim()
  if (!createdAtValue) {
    return { year: '', month: '' }
  }

  const createdAtDate = new Date(createdAtValue)
  if (Number.isNaN(createdAtDate.getTime())) {
    return { year: '', month: '' }
  }

  return {
    year: String(createdAtDate.getUTCFullYear()),
    month: String(createdAtDate.getUTCMonth() + 1).padStart(2, '0'),
  }
}

function seasonYouWatchMonthHeaderLabel(month, includeYear, year) {
  const monthName = monthOptionLabels[Number(month) - 1] || ''
  return includeYear && year ? `${monthName} ${year}` : monthName
}

function buildSeasonYouWatchEntries(watches) {
  if (!Array.isArray(watches) || watches.length === 0) {
    return []
  }

  const watchParts = watches.map((watch) => ({
    watch,
    parts: seasonYouWatchDateParts(watch),
  }))
  const groupKeys = new Set(
    watchParts.map(({ parts }) => `${parts.year || 'unknown'}-${parts.month || 'unknown'}`),
  )

  if (groupKeys.size < 2) {
    return watchParts.map(({ watch }, index) => ({
      type: 'watch',
      key: `watch-${watch.id}`,
      watch,
      hasBorder: index < watches.length - 1,
    }))
  }

  const years = new Set(watchParts.map(({ parts }) => parts.year).filter(Boolean))
  const showYearHeaders = years.size > 1
  const yearHasMonthHeader = new Map()
  watchParts.forEach(({ parts }) => {
    if (parts.year && parts.month) {
      yearHasMonthHeader.set(parts.year, true)
    }
  })
  const entries = []
  let currentYear = null
  let currentMonthKey = null
  let watchIndex = 0

  watchParts.forEach(({ watch, parts }) => {
    const year = parts.year || 'No date'
    const month = parts.month || ''
    const monthKey = `${year}-${month || 'unknown'}`

    if (showYearHeaders && year !== currentYear) {
      entries.push({
        type: 'year',
        key: `year-${year}-${entries.length}`,
        label: year,
        hasMonthHeader: Boolean(yearHasMonthHeader.get(parts.year)),
      })
      currentYear = year
      currentMonthKey = null
    }

    if (month && monthKey !== currentMonthKey) {
      entries.push({
        type: 'month',
        key: `month-${monthKey}-${entries.length}`,
        label: seasonYouWatchMonthHeaderLabel(month, !showYearHeaders, parts.year),
      })
      currentMonthKey = monthKey
    } else if (!month) {
      currentMonthKey = monthKey
    }

    entries.push({
      type: 'watch',
      key: `watch-${watch.id}`,
      watch,
      hasBorder: watchIndex < watches.length - 1,
    })
    watchIndex += 1
  })

  return entries
}

function compareSeasonYouWatches(a, b) {
  const dateCompare = seasonYouWatchSortValue(b).localeCompare(seasonYouWatchSortValue(a))
  if (dateCompare !== 0) {
    return dateCompare
  }

  const episodeNumberCompare = Number(b?.episode?.number || 0) - Number(a?.episode?.number || 0)
  if (episodeNumberCompare !== 0) {
    return episodeNumberCompare
  }

  return Number(b?.id || 0) - Number(a?.id || 0)
}

function replaceSeasonYouWatch(updatedWatch) {
  const normalized = normalizeSeasonYouWatch(updatedWatch)
  if (!normalized) {
    return
  }

  seasonYouWatches.value = seasonYouWatches.value.map((watch) =>
    watch.id === normalized.id ? normalized : watch,
  ).sort(compareSeasonYouWatches)
}

function removeSeasonYouWatchFromList(watchId) {
  seasonYouWatches.value = seasonYouWatches.value.filter((watch) => watch.id !== watchId)
  seasonYouWatchesLoaded.value = true
}

function buildSeasonWatchValue() {
  if (!seasonWatchYear.value) {
    return ''
  }
  if (!seasonWatchMonth.value) {
    return `${seasonWatchYear.value}-00-00`
  }
  if (!seasonWatchDay.value) {
    return `${seasonWatchYear.value}-${seasonWatchMonth.value}-00`
  }
  return `${seasonWatchYear.value}-${seasonWatchMonth.value}-${seasonWatchDay.value}`
}

function validateSeasonWatchDateSelection() {
  if (seasonWatchDay.value && !seasonWatchYear.value && !seasonWatchMonth.value) {
    return 'You have to choose year and month'
  }
  if (seasonWatchDay.value && seasonWatchYear.value && !seasonWatchMonth.value) {
    return 'You have to choose month'
  }
  if (!seasonWatchYear.value && (seasonWatchMonth.value || seasonWatchDay.value)) {
    return 'You have to choose year'
  }
  return ''
}

function setSeasonWatchFormFromWatch(watch) {
  const watched = String(watch?.watched || '').trim()
  const dateMatch = watched.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  seasonWatchYear.value = dateMatch ? dateMatch[1] : ''
  seasonWatchMonth.value = dateMatch && dateMatch[2] !== '00' ? dateMatch[2] : ''
  seasonWatchDay.value = dateMatch && dateMatch[3] !== '00' ? dateMatch[3] : ''
  seasonWatchDatePickerOpen.value = ''
  seasonWatchFormat.value = String(watch?.format || '').trim()
  seasonWatchDescription.value = String(watch?.description || '').trim()
  seasonWatchPublicDescription.value = Boolean(watch?.publicDescription)
  seasonWatchModalError.value = ''
}

function closeSeasonWatchModal(force = false) {
  if (seasonWatchModalPending.value && !force) {
    return
  }
  seasonWatchModalOpen.value = false
  seasonWatchModalError.value = ''
  seasonWatchEditing.value = null
  seasonWatchDatePickerOpen.value = ''
}

function setSeasonWatchToday() {
  const now = new Date()
  seasonWatchYear.value = String(now.getUTCFullYear())
  seasonWatchMonth.value = String(now.getUTCMonth() + 1).padStart(2, '0')
  seasonWatchDay.value = String(now.getUTCDate()).padStart(2, '0')
  seasonWatchDatePickerOpen.value = ''
}

function toggleSeasonWatchDatePicker(pickerName) {
  seasonWatchDatePickerOpen.value = seasonWatchDatePickerOpen.value === pickerName ? '' : pickerName
}

function selectSeasonWatchYear(year) {
  seasonWatchYear.value = String(year || '')
  seasonWatchDatePickerOpen.value = ''
}

function selectSeasonWatchMonth(month) {
  seasonWatchMonth.value = String(month || '')
  seasonWatchDatePickerOpen.value = ''
}

function selectSeasonWatchDay(day) {
  seasonWatchDay.value = String(day || '')
  seasonWatchDatePickerOpen.value = ''
}

function getStoredListingMode() {
  if (typeof window === 'undefined') return '1'

  try {
    const storedMode = window.sessionStorage.getItem(listingModeStorageKey)
    return ['2', '3'].includes(storedMode) ? storedMode : '1'
  } catch {
    return '1'
  }
}

function storeListingMode(mode) {
  if (typeof window === 'undefined') return

  try {
    window.sessionStorage.setItem(listingModeStorageKey, mode)
  } catch {
    // Session storage is a convenience preference; ignore browser storage failures.
  }
}

function buildAuthHeaders() {
  return auth.state.access
    ? { Authorization: `Bearer ${auth.state.access}` }
    : {}
}

function normalizeRatingValue(value) {
  const parsed = Number.parseInt(value, 10)
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 10 ? parsed : null
}

function resetSeasonEpisodeInteractionState() {
  seasonEpisodeWatchState.value = {}
  seasonEpisodeWatchPending.value = {}
  seasonEpisodeInteractionsLoaded.value = false
  seasonEpisodeFavouriteState.value = {}
  seasonEpisodeFavouritePending.value = {}
  seasonEpisodeRatingState.value = {}
  seasonEpisodeRatingPending.value = {}
  seasonEpisodeHoverRatings.value = {}
  expandedSeasonEpisodeOverviewIds.value = {}
  compactSeasonEpisodeRatingPickerEpisode.value = null
  seasonBulkWatchModalOpen.value = false
  seasonBulkWatchCompletedEpisodeCount.value = 0
}

function closeSeasonBulkWatchModal() {
  seasonBulkWatchModalOpen.value = false
}

function episodeInteractionCode(episodeEntry) {
  return String(episodeEntry?.seasonEpisodeNumber || '').trim()
}

function seasonEpisodeOverviewKey(episodeEntry) {
  return episodeInteractionCode(episodeEntry) || String(episodeEntry?.numberText || '')
}

function isSeasonEpisodeOverviewExpanded(episodeEntry) {
  const key = seasonEpisodeOverviewKey(episodeEntry)
  return Boolean(key && expandedSeasonEpisodeOverviewIds.value[key])
}

function hasExpandableSeasonEpisodeOverview(episodeEntry) {
  return hasExpandableOverview(episodeEntry?.overviewText || '', EPISODE_OVERVIEW_CLIP_LIMIT)
}

function displayedSeasonEpisodeOverview(episodeEntry) {
  const overviewText = String(episodeEntry?.overviewText || '').trim()
  if (!hasExpandableSeasonEpisodeOverview(episodeEntry) || isSeasonEpisodeOverviewExpanded(episodeEntry)) {
    return overviewText
  }
  return clipOverviewWithSuffix(overviewText, EPISODE_OVERVIEW_CLIP_LIMIT, '')
}

function toggleSeasonEpisodeOverview(episodeEntry) {
  const key = seasonEpisodeOverviewKey(episodeEntry)
  if (!key) {
    return
  }

  expandedSeasonEpisodeOverviewIds.value = {
    ...expandedSeasonEpisodeOverviewIds.value,
    [key]: !expandedSeasonEpisodeOverviewIds.value[key],
  }
}

function isSeasonEpisodeFavourited(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  return Boolean(code && seasonEpisodeFavouriteState.value[code])
}

function isSeasonEpisodeWatched(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  return Boolean(code && seasonEpisodeWatchState.value[code])
}

function seasonEpisodeWatchButtonText(episodeEntry, short = false) {
  if (isSeasonEpisodeWatched(episodeEntry)) {
    return 'Watched'
  }
  return short ? 'Set as watched' : 'Mark as watched'
}

function seasonEpisodeWatchIconClass(episodeEntry) {
  return isSeasonEpisodeWatched(episodeEntry) ? 'ion-checkmark' : 'ion-plus'
}

function seasonEpisodeFavouriteButtonText(episodeEntry, short = false) {
  if (isSeasonEpisodeFavourited(episodeEntry)) {
    return 'Favourited'
  }
  return short ? 'Favourite' : 'Add to favourites'
}

function seasonEpisodeRatingValue(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  return normalizeRatingValue(code ? seasonEpisodeRatingState.value[code] : null)
}

function seasonEpisodeRatingText(episodeEntry) {
  const ratingValue = seasonEpisodeRatingValue(episodeEntry)
  return ratingValue ? `${ratingValue} / 10` : '/ 10'
}

function seasonEpisodeCompactRatingStarClass(episodeEntry) {
  return [
    'rating-star',
    seasonEpisodeRatingValue(episodeEntry) ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function openCompactSeasonEpisodeRatingPicker(episodeEntry) {
  if (!isAuthenticated.value || !useCompactListMode.value || activeListingMode.value !== '3') return
  compactSeasonEpisodeRatingPickerEpisode.value = episodeEntry
}

function closeCompactSeasonEpisodeRatingPicker() {
  if (compactSeasonEpisodeRatingPickerEpisode.value) {
    resetSeasonEpisodeRatingPreview(compactSeasonEpisodeRatingPickerEpisode.value)
  }
  compactSeasonEpisodeRatingPickerEpisode.value = null
}

async function selectCompactSeasonEpisodeRating(ratingValue) {
  if (!compactSeasonEpisodeRatingPickerEpisode.value) return
  const episodeEntry = compactSeasonEpisodeRatingPickerEpisode.value
  const wasSaved = await setSeasonEpisodeRating(episodeEntry, ratingValue)
  if (wasSaved) {
    closeCompactSeasonEpisodeRatingPicker()
  }
}

async function removeCompactSeasonEpisodeRating() {
  if (!compactSeasonEpisodeRatingPickerEpisode.value) return
  const episodeEntry = compactSeasonEpisodeRatingPickerEpisode.value
  const wasRemoved = await removeSeasonEpisodeRating(episodeEntry)
  if (wasRemoved) {
    closeCompactSeasonEpisodeRatingPicker()
  }
}

function previewSeasonEpisodeRating(episodeEntry, star) {
  const code = episodeInteractionCode(episodeEntry)
  if (!code) return
  seasonEpisodeHoverRatings.value = {
    ...seasonEpisodeHoverRatings.value,
    [code]: star,
  }
}

function resetSeasonEpisodeRatingPreview(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  if (!code || !Object.prototype.hasOwnProperty.call(seasonEpisodeHoverRatings.value, code)) {
    return
  }
  const nextValue = { ...seasonEpisodeHoverRatings.value }
  delete nextValue[code]
  seasonEpisodeHoverRatings.value = nextValue
}

function seasonEpisodeRatingStarClass(episodeEntry, star) {
  const code = episodeInteractionCode(episodeEntry)
  const visibleRating = (
    (code ? seasonEpisodeHoverRatings.value[code] : 0)
    || seasonEpisodeRatingValue(episodeEntry)
    || 0
  )
  return [
    'rating-star',
    star <= visibleRating ? 'rating-star--preview ion-ios-star' : 'ion-ios-star-outline',
  ]
}

function setListingMode(button) {
  if (button === '1' || button === '2' || button === '3') {
    activeListingMode.value = button
    storeListingMode(button)
  }
}

function syncListModeViewport() {
  if (typeof window === 'undefined') return
  useCompactListMode.value = window.innerWidth <= 640
}

function handleSeasonDetailKeydown(event) {
  if (event.key === 'Escape') {
    closeCompactSeasonEpisodeRatingPicker()
  }
}

onMounted(() => {
  syncListModeViewport()
  window.addEventListener('resize', syncListModeViewport)
  window.addEventListener('keydown', handleSeasonDetailKeydown)
})

onBeforeUnmount(() => {
  if (typeof window === 'undefined') return
  window.removeEventListener('resize', syncListModeViewport)
  window.removeEventListener('keydown', handleSeasonDetailKeydown)
})

async function loadSeasonDetail(currentSlug, currentSeasonNumber) {
  const confirmPageView = capturePageView()
  season.value = createEmptySeasonState()
  resetSeasonEpisodeInteractionState()
  seasonFavourited.value = false
  seasonDetailIncludesSeasonFavouriteState.value = false
  seasonLoaded.value = false
  seasonNotFound.value = false
  if (!currentSlug || !currentSeasonNumber || typeof fetch !== 'function') {
    seasonLoaded.value = true
    return
  }

  try {
    const authHeaders = buildAuthHeaders()
    const detailSection = ['comments', 'you'].includes(activeSection.value) ? activeSection.value : ''
    const detailUrl = detailSection
      ? `/api/v1/shows/${currentSlug}/seasons/season-${currentSeasonNumber}/?section=${detailSection}`
      : `/api/v1/shows/${currentSlug}/seasons/season-${currentSeasonNumber}/`
    const response = Object.keys(authHeaders).length > 0
      ? await fetch(detailUrl, {
        headers: authHeaders,
      })
      : await fetch(detailUrl)
    if (response.status === 404) {
      seasonNotFound.value = true
      return
    }
    if (!response.ok) return
    const data = await response.json()
    if (!showSeasonNotFound.value) confirmPageView()
    season.value = {
      id: data.season_id || null,
      title: data.title || '',
      startYear: data.start_year || null,
      endYear: data.end_year || null,
      seasonTitle: data.season_title || '',
      seasonYear: data.season_year || '',
      posterUrl: data.poster_url || posterPlaceholderImage,
      backgroundUrl: data.background_url || defaultBackgroundImage,
      mobileBackgroundUrl: data.mobile_background_url || '',
      overview: data.overview || '',
      episodeCountText: data.episode_count_text || 'Episodes (0)',
      prevSeason: data.prev_season || null,
      nextSeason: data.next_season || null,
      episodes: normalizeEpisodes(data.episodes),
    }
    applySeasonDetailViewerState(data, season.value.episodes)
  } catch {
    // Keep the neutral loading state visible when the API call fails.
  } finally {
    seasonLoaded.value = true
  }
}

async function loadSeasonEpisodeInteractionStatuses(currentSlug, currentSeasonNumber) {
  resetSeasonEpisodeInteractionState()

  if (
    activeSection.value === 'you' ||
    !currentSlug ||
    !currentSeasonNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    seasonEpisodeInteractionsLoaded.value = !isAuthenticated.value
    return
  }

  try {
    const response = await fetch(
      `/api/v1/shows/${currentSlug}/seasons/season-${currentSeasonNumber}/episode-interactions`,
      {
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    const nextWatchState = {}
    const nextFavouriteState = {}
    const nextRatingState = {}

    Object.entries(data.episodes || {}).forEach(([code, state]) => {
      nextWatchState[code] = Boolean(state?.watched)
      nextFavouriteState[code] = Boolean(state?.favourited)
      nextRatingState[code] = normalizeRatingValue(state?.rating)
    })

    seasonEpisodeWatchState.value = nextWatchState
    seasonEpisodeFavouriteState.value = nextFavouriteState
    seasonEpisodeRatingState.value = nextRatingState
  } catch {
    // Keep the episode controls in their default state if the interaction status request fails.
  } finally {
    seasonEpisodeInteractionsLoaded.value = true
  }
}

async function loadSeasonYouWatches(
  currentSlug = slug.value,
  currentSeasonNumber = seasonNumber.value,
  section = activeSection.value,
) {
  if (section !== 'you') {
    return
  }

  seasonYouWatchesError.value = ''

  if (
    !currentSlug ||
    !currentSeasonNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    seasonYouWatches.value = []
    seasonYouWatchesLoaded.value = true
    seasonYouWatchesLoading.value = false
    return
  }

  seasonYouWatchesLoading.value = true
  try {
    const seasonIdQuery = season.value.id ? `?season_id=${encodeURIComponent(season.value.id)}` : ''
    const response = await fetch(
      `/api/v1/shows/${currentSlug}/seasons/season-${currentSeasonNumber}/watches${seasonIdQuery}`,
      {
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      seasonYouWatches.value = []
      seasonYouWatchesLoaded.value = true
      return
    }
    if (!response.ok) {
      seasonYouWatchesError.value = 'Unable to load watches right now.'
      return
    }
    const data = await response.json()
    seasonYouWatches.value = Array.isArray(data.watches)
      ? data.watches.map(normalizeSeasonYouWatch).filter(Boolean).sort(compareSeasonYouWatches)
      : []
    seasonYouWatchesLoaded.value = true
  } catch {
    seasonYouWatchesError.value = 'Unable to load watches right now.'
  } finally {
    seasonYouWatchesLoading.value = false
  }
}

async function loadSeasonFavouriteStatus(currentSlug, currentSeasonNumber) {
  seasonFavourited.value = false

  if (
    !currentSlug ||
    !currentSeasonNumber ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  try {
    const response = await fetch(
      `/api/v1/shows/${currentSlug}/seasons/season-${currentSeasonNumber}/favourite`,
      {
        headers: buildAuthHeaders(),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    seasonFavourited.value = Boolean(data.favourited)
  } catch {
    // Keep the control in the default state if the favourite status request fails.
  }
}

function seasonWatchEndpoint(watch) {
  const code = String(watch?.episode?.seasonEpisodeNumber || '').trim()
  if (!slug.value || !code || !watch?.id) {
    return ''
  }
  return `/api/v1/episodes/${slug.value}/${code}/watches/${watch.id}`
}

function openSeasonWatchEditModal(watch) {
  if (!watch || seasonWatchModalPending.value) {
    return
  }

  seasonWatchEditing.value = watch
  setSeasonWatchFormFromWatch(watch)
  seasonWatchModalOpen.value = true
}

async function removeSeasonYouWatch(watch) {
  const endpoint = seasonWatchEndpoint(watch)
  if (
    !endpoint ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  if (!window.confirm('Remove this watch?')) {
    return
  }

  try {
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) {
      seasonYouWatchesError.value = 'Unable to remove the watch right now.'
      return
    }

    removeSeasonYouWatchFromList(watch.id)
    await loadSeasonEpisodeInteractionStatuses(slug.value, seasonNumber.value)
  } catch {
    seasonYouWatchesError.value = 'Unable to remove the watch right now.'
  }
}

async function saveSeasonWatch() {
  const watch = seasonWatchEditing.value
  const endpoint = seasonWatchEndpoint(watch)
  if (
    seasonWatchModalPending.value ||
    !endpoint ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  seasonWatchModalError.value = ''
  const dateValidationError = validateSeasonWatchDateSelection()
  if (dateValidationError) {
    seasonWatchModalError.value = dateValidationError
    return
  }

  seasonWatchModalPending.value = true
  try {
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        watched: buildSeasonWatchValue(),
        format: seasonWatchFormat.value || null,
        description: seasonWatchDescription.value,
        public_description: seasonWatchPublicDescription.value,
      }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      closeSeasonWatchModal(true)
      return
    }
    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      seasonWatchModalError.value =
        data.description ||
        data.watched ||
        data.format ||
        'Unable to save the watch right now.'
      return
    }

    replaceSeasonYouWatch({
      ...data,
      episode: watch.episode,
    })
    closeSeasonWatchModal(true)
  } catch {
    seasonWatchModalError.value = 'Unable to save the watch right now.'
  } finally {
    seasonWatchModalPending.value = false
  }
}

async function toggleSeasonFavourite() {
  if (
    seasonFavouritePending.value ||
    !slug.value ||
    !seasonNumber.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  seasonFavouritePending.value = true
  try {
    const response = await fetch(
      `/api/v1/shows/${slug.value}/seasons/season-${seasonNumber.value}/favourite`,
      {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    seasonFavourited.value = Boolean(data.favourited)
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    seasonFavouritePending.value = false
  }
}

watch(
  [slug, seasonNumber, isAuthenticated],
  async ([currentSlug, currentSeasonNumber, authenticated]) => {
    seasonYouWatches.value = []
    seasonYouWatchesLoading.value = false
    seasonYouWatchesLoaded.value = activeSection.value === 'you' && !authenticated
    seasonYouWatchesError.value = ''
    await loadSeasonDetail(currentSlug, currentSeasonNumber)
    if (!authenticated) {
      seasonFavourited.value = false
      seasonFavouritePending.value = false
      seasonWatchModalPending.value = false
      closeSeasonWatchModal(true)
      return
    }
    if (!seasonDetailIncludesSeasonFavouriteState.value) {
      loadSeasonFavouriteStatus(currentSlug, currentSeasonNumber)
    }
    if (
      !['comments', 'you'].includes(activeSection.value) &&
      !seasonEpisodeInteractionsLoaded.value
    ) {
      loadSeasonEpisodeInteractionStatuses(currentSlug, currentSeasonNumber)
    }
    loadSeasonYouWatches(currentSlug, currentSeasonNumber, activeSection.value)
  },
  { immediate: true },
)

watch(documentTitle, setDocumentTitle, { immediate: true })
watch(() => season.value.posterUrl, (src) => {
  resetSeasonPosterLoading(src)
}, { immediate: true })

watch(activeSection, async (section) => {
  if (section === 'overview') {
    await loadSeasonDetail(slug.value, seasonNumber.value)
    if (isAuthenticated.value && !seasonEpisodeInteractionsLoaded.value) {
      loadSeasonEpisodeInteractionStatuses(slug.value, seasonNumber.value)
    }
    return
  }
  if (section === 'you') {
    loadSeasonYouWatches(slug.value, seasonNumber.value, section)
  }
})

watch([slug, seasonNumber], () => {
  closeSeasonWatchModal(true)
})

watch(seasonWatchYear, (nextYear) => {
  if (!nextYear) {
    seasonWatchMonth.value = ''
    seasonWatchDay.value = ''
    return
  }
  if (
    seasonWatchDay.value &&
    Number.parseInt(seasonWatchDay.value, 10) > seasonWatchSelectedDayCount.value
  ) {
    seasonWatchDay.value = ''
  }
})

watch(seasonWatchMonth, (nextMonth) => {
  if (!nextMonth) {
    seasonWatchDay.value = ''
    return
  }
  if (
    seasonWatchDay.value &&
    Number.parseInt(seasonWatchDay.value, 10) > seasonWatchSelectedDayCount.value
  ) {
    seasonWatchDay.value = ''
  }
})

async function toggleSeasonEpisodeFavourite(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  if (
    !code ||
    seasonEpisodeFavouritePending.value[code] ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  seasonEpisodeFavouritePending.value = {
    ...seasonEpisodeFavouritePending.value,
    [code]: true,
  }
  try {
    const response = await fetch(`/api/v1/episodes/${slug.value}/${code}/favourite`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'S' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const data = await response.json()
    seasonEpisodeFavouriteState.value = {
      ...seasonEpisodeFavouriteState.value,
      [code]: Boolean(data.favourited),
    }
    if (data.watched === true) {
      seasonEpisodeWatchState.value = {
        ...seasonEpisodeWatchState.value,
        [code]: true,
      }
    }
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    seasonEpisodeFavouritePending.value = {
      ...seasonEpisodeFavouritePending.value,
      [code]: false,
    }
  }
}

async function markSeasonEpisodeWatched(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  if (
    !code ||
    seasonEpisodeWatchPending.value[code] ||
    isSeasonEpisodeWatched(episodeEntry) ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return
  }

  seasonEpisodeWatchPending.value = {
    ...seasonEpisodeWatchPending.value,
    [code]: true,
  }
  try {
    const response = await fetch(`/api/v1/episodes/${slug.value}/${code}/watch`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ source: 'S' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    seasonEpisodeWatchState.value = {
      ...seasonEpisodeWatchState.value,
      [code]: true,
    }
    loadSeasonYouWatches(slug.value, seasonNumber.value, activeSection.value)
  } catch {
    // Leave the current button state unchanged on network or API failure.
  } finally {
    seasonEpisodeWatchPending.value = {
      ...seasonEpisodeWatchPending.value,
      [code]: false,
    }
  }
}

async function markSeasonAsWatched() {
  if (
    seasonBulkWatchPending.value
    || !slug.value
    || !seasonNumber.value
    || !isAuthenticated.value
    || !seasonBulkWatchActionVisible.value
    || typeof fetch !== 'function'
  ) {
    return
  }

  const shouldMarkSeasonAsWatched = window.confirm(
    `Are you sure you want mark every episode (${seasonTotalEpisodeCount.value}) of Season ${seasonNumber.value} as watched?`,
  )
  if (!shouldMarkSeasonAsWatched) {
    return
  }

  seasonBulkWatchPending.value = true
  try {
    const response = await fetch(
      `/api/v1/shows/${slug.value}/seasons/season-${seasonNumber.value}/watch`,
      {
        method: 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ source: 'S' }),
      },
    )
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return
    }
    if (!response.ok) return
    const nextState = { ...seasonEpisodeWatchState.value }
    seasonWatchableEpisodes.value.forEach((episode) => {
      const code = episodeInteractionCode(episode)
      if (code) {
        nextState[code] = true
      }
    })
    seasonEpisodeWatchState.value = nextState
    seasonBulkWatchCompletedEpisodeCount.value = seasonTotalEpisodeCount.value
    seasonBulkWatchModalOpen.value = true
    loadSeasonYouWatches(slug.value, seasonNumber.value, activeSection.value)
  } catch {
    // Leave the current season watch state unchanged on network or API failure.
  } finally {
    seasonBulkWatchPending.value = false
  }
}

async function setSeasonEpisodeRating(episodeEntry, ratingValue) {
  const code = episodeInteractionCode(episodeEntry)
  if (
    !code ||
    seasonEpisodeRatingPending.value[code] ||
    !slug.value ||
    !isAuthenticated.value ||
    typeof fetch !== 'function'
  ) {
    return false
  }

  seasonEpisodeRatingPending.value = {
    ...seasonEpisodeRatingPending.value,
    [code]: true,
  }
  try {
    const response = await fetch(`/api/v1/episodes/${slug.value}/${code}/rating`, {
      method: 'POST',
      headers: {
        ...buildAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ rating: ratingValue, source: 'S' }),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return false
    }
    if (!response.ok) return false
    const data = await response.json()
    seasonEpisodeRatingState.value = {
      ...seasonEpisodeRatingState.value,
      [code]: normalizeRatingValue(data.rating),
    }
    if (data.watched === true) {
      seasonEpisodeWatchState.value = {
        ...seasonEpisodeWatchState.value,
        [code]: true,
      }
    }
    resetSeasonEpisodeRatingPreview(episodeEntry)
    return true
  } catch {
    // Leave the current rating unchanged on network or API failure.
    return false
  } finally {
    seasonEpisodeRatingPending.value = {
      ...seasonEpisodeRatingPending.value,
      [code]: false,
    }
  }
}

async function removeSeasonEpisodeRating(episodeEntry) {
  const code = episodeInteractionCode(episodeEntry)
  if (
    !code ||
    seasonEpisodeRatingPending.value[code] ||
    !slug.value ||
    !isAuthenticated.value ||
    !seasonEpisodeRatingValue(episodeEntry) ||
    typeof fetch !== 'function'
  ) {
    return false
  }

  seasonEpisodeRatingPending.value = {
    ...seasonEpisodeRatingPending.value,
    [code]: true,
  }
  try {
    const response = await fetch(`/api/v1/episodes/${slug.value}/${code}/rating`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    })
    if (response.status === 401 || response.status === 403) {
      auth.clearTokens()
      return false
    }
    if (!response.ok) return false
    seasonEpisodeRatingState.value = {
      ...seasonEpisodeRatingState.value,
      [code]: null,
    }
    resetSeasonEpisodeRatingPreview(episodeEntry)
    return true
  } catch {
    // Leave the current rating unchanged on network or API failure.
    return false
  } finally {
    seasonEpisodeRatingPending.value = {
      ...seasonEpisodeRatingPending.value,
      [code]: false,
    }
  }
}

watch([useCompactListMode, activeListingMode], ([compact, listingMode]) => {
  if (!compact || listingMode !== '3') {
    closeCompactSeasonEpisodeRatingPicker()
  }
})

useMetaDescription(() => buildMetaDescription(
  'season',
  hasLoadedSeasonPayload.value ? { title: showTitle.value, number: seasonNumber.value } : {},
  activeSection.value,
))
</script>
